/**
 * CPU 対戦の設定画面 (`/local/new`)。
 *
 * - ゲーム構成プリセット (Animal+Reversi / BG x3 / etc)
 * - クロックモード (WholePolicy / PerBoardPolicy / ByoYomi)
 * - CPU 強さ (Easy / Normal / Hard)
 * - 自分の先後選好 (random / host / guest)
 *
 * 「対局開始」 を押すと UUID を発番して `/local/:id` に遷移、
 * LocalConnection が立ち上がる。
 */

import { useEffect, useState } from 'react';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { GamePicker, MULTI_PRESETS, SINGLE_GAMES } from '../components/GamePicker';
import { ModePicker, MODE_PRESETS } from '../components/ModePicker';
import { TitleBar } from '../components/TitleBar';
import { singleGameCpuConfig, startCpuMatch } from '../lib/local-match';
import type { MatchConfig } from '../proto';
import type { CpuLevel } from '../wasm/cpu';
import { ensureCpuWasm } from '../wasm/cpu';

const LEVELS: { id: CpuLevel; label: string; hint: string }[] = [
  { id: 'easy', label: 'Easy', hint: '入門 (ランダム手寄り)' },
  { id: 'normal', label: 'Normal', hint: '普通 (基本評価関数)' },
  { id: 'hard', label: 'Hard', hint: 'やや強い (深め探索)' },
];

/** 初期構成: `?game=Chess` のような query があればその単一ゲーム、 無ければ 囲碁・将棋・リバーシ。
 *  単独盤は sequential、 複数盤は parallel を既定モードにする。 */
function initialConfig(): MatchConfig {
  const g = new URLSearchParams(window.location.search).get('game');
  const single = g ? SINGLE_GAMES.find((s) => s.key === g) : null;
  if (single) return singleGameCpuConfig(single.key);
  const preset = MULTI_PRESETS.find((p) => p.id === 'classic-3')!;
  const sel = { template: preset.template, games: preset.games };
  const mode = MODE_PRESETS.find((m) => m.id === (sel.games.length > 1 ? 'parallel' : 'sequential'))!;
  return {
    template: sel.template,
    games: sel.games,
    orchestrator: mode.orchestrator,
    clock: {
      scope: mode.scope,
      increment: { type: 'Fischer', increment_ms: 3000 },
      // CPU 戦は時間を競う場ではないので、 事実上切れない長さに固定する
      // (選ばせない)。 対人戦の持ち時間は部屋側の設定で決める。
      initial_ms: 2 * 60 * 60 * 1000,
    },
    win: { kind: 'MajorityOfBoards' },
    // CPU 戦は engine の auto-finish (= state.outcome 確定) を OFF。 state.outcome を
    // engine に立てさせず、 終局の確定 (finish) は local-connection が行う。 判定自体は
    // engine の match_outcome (MajorityOfBoards) をそのまま使うので、 対人戦と同じく
    // 過半数 (3 盤なら 2 勝) が確定した時点でマッチは終わる。
    auto_finish: false,
  };
}

export function CpuSetup() {
  const [config, setConfig] = useState<MatchConfig>(() => initialConfig());
  // Shogi 単独盤は sequential 既定で問題なし
  const [level, setLevel] = useState<CpuLevel>('normal');

  // CpuSetup を開いた時点で CPU Worker を warm up。 「開始」 直後の最初の手で
  // worker 起動 + WASM フェッチを待たされて 「CPU が動かない」 と見えるのを回避。
  // (Playwright 完全テストスイートで HU Poker test が稀に flaky だった原因対策)
  useEffect(() => {
    void ensureCpuWasm().catch(() => {});
  }, []);

  const startMatch = () => {
    if (config.games.length === 0 || config.games.length > 8) return;
    startCpuMatch(config, level);
  };

  return (
    <main className="app-shell cpu-setup">
      <TitleBar
        title="CPU 対戦の設定"
        back={{ href: '/' }}
        center={<ConnectionBadge />}
      />

      <section className="cpu-actions">
        <button className="btn-primary" disabled={config.games.length === 0 || config.games.length > 8} onClick={startMatch}>
          対局開始
        </button>
      </section>

      <section className="setup-section">
        <h3>対局構成</h3>
        <GamePicker config={config} onChange={setConfig} showLabel={false} />
      </section>

      <section className="setup-section">
        <h3>進行モード</h3>
        <ModePicker config={config} onChange={setConfig} />
      </section>

      <section className="setup-section">
        <h3>CPU 強さ</h3>
        <div className="time-control-options">
          {LEVELS.map((l) => (
            <button
              key={l.id}
              className={`time-control-option ${level === l.id ? 'active' : ''}`}
              onClick={() => setLevel(l.id)}
              data-testid={`cpu-level-${l.id}`}
            >
              <strong>{l.label}</strong>
              <small className="muted"> {l.hint}</small>
            </button>
          ))}
        </div>
      </section>

      <section className="cpu-actions">
        <button className="btn-primary" disabled={config.games.length === 0 || config.games.length > 8} onClick={startMatch} data-testid="cpu-start">
          対局開始
        </button>
      </section>
    </main>
  );
}
